import { useState } from 'react';
import { useAsyncEffect } from 'rooks';
import { logError } from '@/utilities/logger';
import { isAxiosError } from '@seeqdev/api';
import { sqJobsApi } from '@seeqdev/api/endpoint/JobsApi';
import { SeeqNames } from '@seeqdev/constants';
import type { JobOutputV1 } from '@seeqdev/api/model/JobOutputV1';

/**
 * The condition monitor's scheduled Quartz job, or null when it has none (a stopped monitor). Refetches when
 * `refetchKey` changes, e.g. once per finished run, since a run can create or remove the job.
 */
export function useConditionMonitorJob(conditionMonitorId?: string, refetchKey?: string) {
  const [conditionMonitorJob, setConditionMonitorJob] = useState<JobOutputV1 | null>();

  useAsyncEffect(
    async (shouldContinueEffect: () => boolean) => {
      if (!conditionMonitorId) {
        setConditionMonitorJob(undefined);
        return;
      }
      try {
        const { data: job } = await sqJobsApi.getJob({
          group: SeeqNames.JobGroup.ConditionMonitor,
          id: `${SeeqNames.JobGroup.ConditionMonitor}-${conditionMonitorId.toLowerCase()}`,
        });
        if (shouldContinueEffect()) {
          setConditionMonitorJob(job);
        }
      } catch (e) {
        if (!shouldContinueEffect()) {
          return;
        }
        // A stopped monitor has no scheduled job, so a 404 is the expected answer rather than an error.
        if (!(isAxiosError(e) && e.response?.status === 404)) {
          logError(e as Error);
        }
        setConditionMonitorJob(null);
      }
    },
    [conditionMonitorId, refetchKey],
  );

  return conditionMonitorJob;
}
